import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GlassCard from '../Ui/GlassCard';
import { motion, AnimatePresence } from 'framer-motion';
import { makeTrace } from '@/utils/operationalTrace';
import rtClient, { subscribeOperationChange, verifyOperationConsistency } from '@/api/realtimeClient';
import { sanitizeOperationId } from '@/shared/operationId.js';
import { useAuth } from '@/api/AuthContext';
import { safeText } from '@/utils/displaySafe';
import { participantDisplayName } from '@/utils/participantDisplay';
import { playProceduralSound } from '../System/SoundManager';
import FirstAccessGate from '../Auth/FirstAccessGate';
import { isShowcaseRoom, isShowcaseVisitor, showcaseRooms } from '@/utils/consoleAccess';

const FALLBACK_OPERATION_ID = null;
const NO_OPERATION_LABEL = 'SEM OPERAÇÃO';

// Sprint A — Gestao Operacional de Participantes: papeis funcionais SCI
// atribuiveis dinamicamente por operacao (ver
// src/backend/rbac/constants_roles.js::SCI_FUNCTIONAL_ROLES). Mantido em
// espelho manual e curto propositalmente — nao ha endpoint publico para
// listar roles canonicos e duplicar 7 strings aqui e mais simples que criar
// um novo endpoint so para isso.
const ASSIGNABLE_OPERATIONAL_ROLES = [
    { value: 'observer', label: 'Observador' },
    { value: 'incident_commander', label: 'Comandante do Incidente' },
    { value: 'operations_chief', label: 'Operações' },
    { value: 'logistics', label: 'Logística' },
    { value: 'safety', label: 'Segurança Operacional (Safety)' },
    { value: 'communications', label: 'Comunicações' },
    { value: 'liaison', label: 'Ligação' },
    { value: 'finance_admin', label: 'Finanças/Administração' },
    { value: 'intelligence_officer', label: 'Oficial de Inteligência' },
];

function operationalRoleLabel(role) {
    return ASSIGNABLE_OPERATIONAL_ROLES.find((r) => r.value === role)?.label || String(role).replace(/_/g, ' ');
}

function normalizeOperationIdentifier(value) {
    const sanitized = sanitizeOperationId(value);
    if (!sanitized) return '';
    if (sanitized === FALLBACK_OPERATION_ID) return '';
    return sanitized;
}

function getStoredOperationIdentifier() {
    try {
        if (typeof localStorage === 'undefined') return '';
        return normalizeOperationIdentifier(localStorage.getItem('reacta_operationId'));
    } catch (error) {
        return '';
    }
}

function getPrimaryOperationLabel(value) {
    const normalized = normalizeOperationIdentifier(value);
    return safeText(normalized || NO_OPERATION_LABEL, {
        fallback: NO_OPERATION_LABEL,
        emptyFallback: NO_OPERATION_LABEL,
        invalidFallback: NO_OPERATION_LABEL,
    });
}

function normalizeIdentityValue(value) {
    return safeText(value, {
        fallback: '',
        emptyFallback: '',
        invalidFallback: '',
    }).trim().toLowerCase();
}

function getSessionIdentifier() {
    try {
        return localStorage.getItem('reacta_sessionId') || sessionStorage.getItem('reacta_sessionId') || '';
    } catch (error) {
        return '';
    }
}

function getUserIdentityKeys(user) {
    return new Set([
        user?.name,
        user?.operatorId,
        user?.operator_id,
        user?.email,
        user?.username,
    ].map(normalizeIdentityValue).filter(Boolean));
}

function isCurrentUserPayload(payload = {}, user) {
    const keys = getUserIdentityKeys(user);
    if (!keys.size) return false;
    return [
        payload.operatorId,
        payload.presenceId,
        payload.name,
        payload.email,
        payload.username,
    ].some((value) => keys.has(normalizeIdentityValue(value)));
}

export default function StagingRoom({ isOpen, onClose }) {
    const { user } = useAuth(); // Fonte da verdade para identidade
    const [visible, setVisible] = useState(isOpen);
    const [ready, setReady] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [eventFeed, setEventFeed] = useState([]);
    const [operationId, setOperationId] = useState(() => normalizeOperationIdentifier(rtClient.getActiveOperationId()) || getStoredOperationIdentifier());
    const [joined, setJoined] = useState(false);
    const [changingOperation, setChangingOperation] = useState(false);
    const [admissionStatus, setAdmissionStatus] = useState('waiting'); // waiting, admitted, denied
    const [liveMode, setLiveMode] = useState(false);
    const [pendingUsers, setPendingUsers] = useState([]);
    const [operationalRoles, setOperationalRoles] = useState({}); // operatorId -> role (Sprint A)
    const previousAdmissionStatus = useRef(admissionStatus);

    useEffect(() => {
        if (previousAdmissionStatus.current !== 'admitted' && admissionStatus === 'admitted') {
            playProceduralSound('success');
        }
        previousAdmissionStatus.current = admissionStatus;
    }, [admissionStatus]);

    useEffect(() => setVisible(isOpen), [isOpen]);

    // A lógica de restauração de identidade foi centralizada no AuthContext.
    // Este useEffect não é mais necessário.

    // Note: do NOT return early before declaring all hooks — keep render hooks order stable.
    // The UI will still conditionally render content below using the `visible` flag.

    useEffect(() => {
        // try to use realtime client if available
        let mounted = true;
        const unsubscribers = [];
        let rtModule = null;
        (async () => {
            try {
                rtModule = { default: rtClient, ...rtClient };
                // await rtClient.connect(); // REMOVIDO PELA AUDITORIA - A conexão agora é centralizada no AccessGateway.
                const updateLive = () => {
                    if (!mounted || !rtModule) return;
                    setLiveMode(rtClient.isLiveOperation());
                };
                
                // O TIMING FIX foi removido pois a causa raiz da condição de corrida foi resolvida.
                
                const ps = rtClient.getParticipants() || [];
                if (mounted) setParticipants(ps);
                if (mounted && ps.some((participant) => participant.admitted && isCurrentUserPayload(participant, user))) {
                    setAdmissionStatus('admitted');
                }
                if (mounted) setEventFeed(rtClient.getEventFeed().slice(0, 6));
                updateLive();
                unsubscribers.push(rtClient.on('USER_CONNECTED', (p) => mounted && setParticipants((s) => [...s.filter(x => x.operatorId !== p.operatorId), p])));
                unsubscribers.push(rtClient.on('USER_DISCONNECTED', (p) => mounted && setParticipants((s) => s.map(x => x.operatorId === p.operatorId ? { ...x, ...p } : x))));
                unsubscribers.push(rtClient.on('READY_STATE_CHANGED', (p) => mounted && setParticipants((s) => s.map(x => x.operatorId === p.operatorId ? { ...x, ready: p.ready } : x))));
                unsubscribers.push(rtClient.on('PRESENCE_UPDATED', (snapshot) => {
                    if (!mounted) return;
                    const nextParticipants = snapshot.participants || [];
                    setParticipants(nextParticipants);
                    setEventFeed((snapshot.feed || []).slice(0, 6));
                    if (nextParticipants.some((participant) => participant.admitted && isCurrentUserPayload(participant, user))) {
                        setAdmissionStatus('admitted');
                    }
                    updateLive();
                }));
                unsubscribers.push(rtClient.on('OPERATION_FEED', () => mounted && setEventFeed(rtClient.getEventFeed().slice(0, 6))));
                unsubscribers.push(rtClient.on('OPERATION_JOINED', (p) => {
                    if (!mounted) return;
                    try {
                        if (p && p.operationId) {
                            setOperationId(normalizeOperationIdentifier(p.operationId));
                            verifyOperationConsistency('StagingRoom', p.operationId);
                        }
                    } catch (e) { }
                }));
                unsubscribers.push(rtClient.on('SESSION_RESTORED', (payload) => {
                    if (!mounted) return;
                    try {
                        const restoredOperationId = payload?.operationId || payload?.operationalState?.operationId;
                        if (restoredOperationId) {
                            setOperationId(normalizeOperationIdentifier(restoredOperationId));
                            verifyOperationConsistency('StagingRoom', restoredOperationId);
                        }
                    } catch (e) { }
                }));
                // Listen for admission events
                unsubscribers.push(rtClient.on('USER_ADMITTED', (payload) => {
                    if (!mounted) return;
                    if (isCurrentUserPayload(payload, user)) {
                        setAdmissionStatus('admitted');
                    }
                }));
            } catch (e) {
                console.warn('[STAGING] Realtime connection or participant fetch failed. Displaying "REALTIME INDISPONÍVEL".', e);
                setParticipants([]); // Definir como array vazio conforme política WYSIWYG
            }
        })();
        return () => {
            mounted = false;
            unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
            rtModule = null;
        };
    }, [user]);

    useEffect(() => {
        const unsubscribe = subscribeOperationChange((nextOperationId) => {
            setOperationId(normalizeOperationIdentifier(nextOperationId));
            verifyOperationConsistency('StagingRoom', nextOperationId);
        });
        return unsubscribe;
    }, []);

    // Sprint A — Gestao Operacional de Participantes: busca a atribuicao de
    // papel operacional (USER x OPERATION) uma vez ao abrir/trocar de
    // operacao, e mantem sincronizada via o evento realtime ROLE_ASSIGNED
    // (ver operationalRoleRoutes.js / socketServer.js::updateOperatorRoleLive).
    // Merge client-side com a lista de PRESENCE_SNAPSHOT — nao altera o shape
    // dessa lista, so complementa.
    const fetchOperationalRoles = async (opId) => {
        if (!opId) return;
        try {
            const sessionId = getSessionIdentifier();
            const url = `/api/rooms/${encodeURIComponent(opId)}/roles`;
            // Credencial no header, nunca na URL (FIELD-P3).
            const resp = await fetch(url, { credentials: 'include', headers: sessionId ? { 'x-session-id': sessionId } : {} });
            if (!resp.ok) return;
            const payload = await resp.json();
            const nextRoles = {};
            (payload?.assignments || []).forEach((a) => { if (a.operatorId) nextRoles[a.operatorId] = a.role; });
            setOperationalRoles(nextRoles);
        } catch (e) {
            console.debug('[STAGING] fetchOperationalRoles failed', e?.message || e);
        }
    };

    useEffect(() => {
        if (visible && operationId) fetchOperationalRoles(operationId);
    }, [visible, operationId]);

    useEffect(() => {
        const off = rtClient.on('ROLE_ASSIGNED', (payload = {}) => {
            if (!payload.targetOperatorId) return;
            setOperationalRoles((prev) => ({ ...prev, [payload.targetOperatorId]: payload.newRole }));
        });
        return () => { off && off(); };
    }, []);

    const operationTitleLabel = getPrimaryOperationLabel(operationId);

    const userRole = user?.role || 'SEM ROLE';
    // 2026-08-05: 'professor' adicionado — mesmo bypass institucional de
    // simulation_manager (pode criar sala, admitir Aluno). Aluno em si NAO
    // entra nesta lista: cai no branch padrao "aguarda admissao" mais
    // abaixo, igual a Operador hoje.
    // Sala de demonstracao: sem espera e sem conduzir a sala, qualquer que
    // seja o papel do visitante (presenca nao e gravada; ver socketServer.js).
    const showcaseVisitor = isShowcaseVisitor(user, operationId);
    const isPrivileged = !showcaseVisitor && ['webmaster', 'institutional_owner', 'super_admin', 'simulation_manager', 'Commander', 'professor'].includes(userRole);
    const isInviteMode = localStorage.getItem('reacta_inviteMode') === 'true';
    // Convidado nao passa pelo seletor de salas: as salas de demonstracao
    // aparecem aqui, com caminho de volta para a sala do convite.
    const inviteRoomRef = useRef('');
    if (!inviteRoomRef.current && operationId && !isShowcaseRoom(user, operationId)) inviteRoomRef.current = operationId;
    const switchToRoom = (nextOperationId) => {
        setOperationId(nextOperationId);
        setJoined(false);
        setAdmissionStatus('waiting');
    };
    // Sprint C: convidados externos (representante de agencia, imprensa)
    // seguem o mesmo fluxo de espera por admissao que Observer/guest —
    // nunca o bypass de isPrivileged.
    const isGuest = userRole === 'Observer' || userRole === 'guest' || userRole === 'guest_agency' || userRole === 'press';
    // Sprint A: quem pode atribuir papel operacional — Gestor da Operacao
    // (mesmo bypass institucional de isPrivileged) ou o Incident Commander
    // ja designado nesta operacao (Tier 2, ver operationalRoleService.js).
    const canAssignRoles = isPrivileged || user?.operationalRole === 'incident_commander';

    useEffect(() => {
        try {
            const privileged = ['webmaster', 'institutional_owner', 'super_admin', 'simulation_manager', 'professor'].includes(userRole);
            if (!privileged) return;
            (async () => {
                try {
                    const r = await fetch('/api/status');
                    if (r.ok) {
                        const js = await r.json();
                        if (js.runtime) document.documentElement.setAttribute('data-orion-runtime', js.runtime);
                    }
                } catch (e) { /* ignore */ }
            })();
        } catch (e) { /* ignore */ }
    }, [userRole]);

    // Build participantsList: prefer realtime, fallback to authenticated user if available
    let participantsList = [];
    
    if (participants && participants.length) {
        // Use realtime participants
        participantsList = participants;
    } else {
        // Se não há participantes do realtime, e não há usuário autenticado, a lista permanece vazia.
        // Não criar participantes fictícios conforme política WYSIWYG.
        // A UI deve exibir "SEM PARTICIPANTES" ou "REALTIME INDISPONÍVEL" se a lista estiver vazia.
    }

    const handleReady = () => {
        playProceduralSound('button');
        setReady((r) => !r);
        // small mock flag
        window.stagingReady = !ready;
        // notify realtime if available
        try {
            rtClient.sendReady(operationId, !ready);
        } catch (e) { }
        try { makeTrace && makeTrace({ eventType: 'READY_STATE', operatorId: user?.name, role: user?.role, sourceModule: 'StagingRoom' }); } catch (e) { }
    };

    const handleEnterOperation = async () => {
        if (admissionStatus !== 'admitted') return;
        playProceduralSound('activate');
        await new Promise((r) => setTimeout(r, 800));
        window.enterCoreRequested = true;
        try { makeTrace && makeTrace({ eventType: 'CORE_ENTER', operatorId: user?.name, role: user?.role, sourceModule: 'StagingRoom' }); } catch (e) { }

        // A persistência de contexto agora é centralizada no AuthContext e não é mais necessária aqui.

        onClose && onClose();
        let targetPath = '/IncidentCommander';
        if (showcaseVisitor) {
            targetPath = '/IncidentCommander';
        } else if (userRole === 'Commander' || userRole === 'simulation_manager') {
            targetPath = '/SimulationManager';
        } else if (userRole === 'Observer' || userRole === 'observer' || userRole === 'guest_agency' || userRole === 'press') {
            targetPath = '/ParticipantNavigator';
        }
        setTimeout(() => {
            window.history.pushState({}, '', targetPath);
            window.dispatchEvent(new PopStateEvent('popstate'));
        }, 300);
    };

    const joinOperationRoom = async () => {
        playProceduralSound('activate');
        try {
            // A conexão já foi estabelecida pelo AuthContext. Apenas entramos na sala.
            rtClient.setOperationId(operationId);
            rtClient.joinOperation(operationId);
            setJoined(true);
            setChangingOperation(false);
            if (showcaseVisitor) {
                setAdmissionStatus('admitted');
                return;
            }

            // PRIVILEGED USERS BYPASS STAGING - Enter directly like Zoom/Google Meet hosts
            if (isPrivileged) {
                console.log('[STAGING] Privileged user detected - bypassing staging room');

                // P5-B: entrar na sala nao altera o estado operacional. A
                // ativacao (operationStatus/commandMode) e decisao explicita
                // de quem conduz: Gestor de Simulacao ou barra de comando.

                // Mark as host/commander in localStorage
                try {
                    localStorage.setItem('reacta_isOperationHost', 'true');
                    localStorage.setItem('reacta_hostAssignedAt', new Date().toISOString());
                } catch (e) { }

                // Emit host assignment event
                try {
                    rtClient.emit('OPERATIONAL_EVENT', {
                        type: 'HOST_ASSIGNED',
                        operationId,
                        operatorId: user?.name || 'unknown',
                        role: userRole,
                        detail: 'Operador privilegiado assumiu comando da operação',
                        timestamp: new Date().toISOString()
                    });
                } catch (e) { }

                // Trace the event
                try {
                    makeTrace && makeTrace({
                        eventType: 'OPERATION_STARTED',
                        operatorId: user?.name,
                        role: userRole,
                        sourceModule: 'StagingRoom',
                        details: { operationId, privilegedBypass: true, role: userRole }
                    });
                } catch (e) { }

                // BYPASS STAGING - Navigate directly to operational console
                console.log('[STAGING] Navigating privileged user directly to operational console');

                // A persistência de contexto agora é centralizada no AuthContext e não é mais necessária aqui.

                onClose && onClose();

                let targetPath = '/IncidentCommander';
                if (userRole === 'simulation_manager' || userRole === 'professor') {
                    targetPath = '/SimulationManager';
                } else if (userRole === 'webmaster' || userRole === 'super_admin') {
                    targetPath = '/GodMode';
                } else if (userRole === 'webmaster') {
                    targetPath = '/AdminConsole';
                }

                setTimeout(() => {
                    window.history.pushState({}, '', targetPath);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                }, 300);

                return; // Exit early - no staging wait
            }

            // GUESTS WITH INVITE: Wait in staging room for admission
            if (isGuest || isInviteMode) {
                console.log('[STAGING] Guest user - waiting for host admission in staging room');
                setAdmissionStatus('waiting');

                // Emit guest arrival notification to hosts
                try {
                    rtClient.emit('GUEST_ARRIVAL', {
                        operationId,
                        operatorId: user?.name || 'guest',
                        role: userRole,
                        timestamp: new Date().toISOString()
                    });
                } catch (e) {
                    console.warn('[STAGING] Failed to notify guest arrival', e);
                }

                return; // Stay in staging room
            }

            // OTHER NON-PRIVILEGED USERS: Wait for admission from host
            console.log('[STAGING] Non-privileged user - waiting for host admission');
            setAdmissionStatus('waiting');
        } catch (e) {
            console.warn('joinOperation failed', e);
        }
    };

    // Admissao so aparece confirmada apos o ack do servidor (decisao
    // persistida); falha fica visivel em vez de um ADMITIDO local.
    const [admitError, setAdmitError] = useState(null);
    const handleAdmitUser = async (operatorId) => {
        setAdmitError(null);
        const result = await rtClient.emitWithAck('ADMIT_USER', { operatorId, operationId });
        if (!result?.ok) {
            playProceduralSound('alert');
            setAdmitError('Admissão não confirmada pelo servidor. Tente novamente.');
            return;
        }
        playProceduralSound('success');
        setParticipants((s) => s.map(x => x.operatorId === operatorId ? { ...x, admitted: true, admissionStatus: 'admitted' } : x));
    };

    // Sprint A — Gestao Operacional de Participantes: atribuicao de papel
    // operacional. O backend (operationalRoleRoutes.js/operationalRoleService.js)
    // e a fonte de verdade da autorizacao — este handler apenas envia a
    // intencao; um retorno nao-ok simplesmente nao atualiza o estado local.
    const handleAssignRole = async (targetOperatorId, role) => {
        if (!operationId || !targetOperatorId || !role) return;
        try {
            const sessionId = getSessionIdentifier();
            const url = `/api/rooms/${encodeURIComponent(operationId)}/roles/assign`;
            const resp = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...(sessionId ? { 'x-session-id': sessionId } : {}) },
                body: JSON.stringify({ targetOperatorId, role }),
            });
            if (!resp.ok) {
                playProceduralSound('error');
                console.warn('[STAGING] role assignment rejected', resp.status);
                return;
            }
            playProceduralSound('success');
            setOperationalRoles((prev) => ({ ...prev, [targetOperatorId]: role }));
        } catch (e) {
            playProceduralSound('error');
            console.warn('Failed to assign operational role', e);
        }
    };

    // Com troca de senha pendente o socket nao conecta (AuthContext) e a
    // espera nunca termina. A sala de espera vive fora do Layout, que e
    // quem mostra o FirstAccessGate; aqui ele precisa ser mostrado tambem.
    if (visible && user?.mustChangePassword === true) {
        return createPortal(<FirstAccessGate />, document.body);
    }

    // Uma unica proxima acao por estado: quem chega nunca precisa escolher
    // entre botoes parecidos. Gestor ja admitido mantem "Assumir comando"
    // como acao secundaria (ativa a operacao e abre o console de gestor).
    const isAdmitted = admissionStatus === 'admitted';
    const isInRoom = joined || isAdmitted || participants.some((p) => isCurrentUserPayload(p, user));
    const isWaitingAdmission = isInRoom && !isAdmitted && !isPrivileged && admissionStatus !== 'denied';
    const showOperationInput = !operationId || changingOperation;

    let nextStepMessage = 'Clique em Entrar na sala para avisar o gestor que você chegou.';
    if (!operationId) nextStepMessage = 'Informe o código da operação para continuar.';
    else if (showcaseVisitor) nextStepMessage = isAdmitted ? 'Tudo certo. Você vai percorrer a sala em visualização passiva.' : 'Sala de demonstração: entrada livre, sem comandos.';
    else if (admissionStatus === 'denied') nextStepMessage = 'Seu acesso a esta operação foi negado. Fale com o gestor da sala.';
    else if (isAdmitted) nextStepMessage = 'Tudo certo. Sua entrada foi liberada.';
    else if (isPrivileged) nextStepMessage = 'Você pode entrar direto e conduzir esta operação.';
    else if (isWaitingAdmission) nextStepMessage = 'Aguarde: o gestor da sala vai liberar sua entrada. Esta tela atualiza sozinha.';

    const steps = [
        { label: 'Entrar na sala', done: isInRoom },
        ...(showcaseVisitor ? [] : [{ label: 'Liberação do gestor', done: isAdmitted }]),
        { label: 'Entrar na operação', done: false },
    ];

    const primaryAction = isAdmitted
        ? { label: 'ENTRAR NA OPERAÇÃO', onClick: handleEnterOperation, disabled: false }
        : isWaitingAdmission
            ? { label: 'AGUARDANDO LIBERAÇÃO', onClick: undefined, disabled: true }
            : { label: 'ENTRAR NA SALA', onClick: joinOperationRoom, disabled: !operationId || admissionStatus === 'denied' };

    return createPortal(
        <AnimatePresence>
            {visible && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose}></div>
                    <GlassCard className="relative z-10 w-full max-w-2xl max-h-full overflow-y-auto p-6" size="lg">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-2xl font-bold text-cyan-200 tracking-wide">Sala de espera</h2>
                                <div className="mt-1 font-mono text-sm text-amber-300">{operationTitleLabel}</div>
                            </div>
                            {!isInviteMode && operationId && !showOperationInput && (
                                <button
                                    onMouseEnter={() => playProceduralSound('button')}
                                    onClick={() => setChangingOperation(true)}
                                    className="text-xs text-gray-400 underline hover:text-cyan-200 transition-colors"
                                >
                                    Trocar de operação
                                </button>
                            )}
                        </div>

                        {showOperationInput && (
                            <label className="mt-4 block">
                                <span className="text-xs text-gray-400">Código da operação</span>
                                <input
                                    value={operationId}
                                    onChange={(e) => setOperationId(e.target.value)}
                                    disabled={isInviteMode}
                                    placeholder="Ex.: SCI26-OP-8TEI"
                                    className={`mt-1 w-full p-2 bg-black/20 rounded font-mono text-sm text-cyan-100 border border-cyan-600/30 ${isInviteMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title={isInviteMode ? 'Sala definida pelo convite' : 'Código da operação'}
                                />
                            </label>
                        )}

                        {isInviteMode && (
                            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-gray-400">Salas de demonstração:</span>
                                {showcaseRooms(user).filter((room) => room.operationId !== operationId).map((room) => (
                                    <button key={room.operationId} type="button" onClick={() => switchToRoom(room.operationId)} className="rounded border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-amber-200 hover:border-amber-300">
                                        {room.label}
                                    </button>
                                ))}
                                {isShowcaseRoom(user, operationId) && inviteRoomRef.current && (
                                    <button type="button" onClick={() => switchToRoom(inviteRoomRef.current)} className="rounded border border-cyan-400/40 px-2 py-1 text-cyan-200 hover:border-cyan-300">
                                        Voltar à sala do convite
                                    </button>
                                )}
                            </div>
                        )}

                        {!isPrivileged && (
                            <ol className="mt-4 grid grid-cols-3 gap-2">
                                {steps.map((step, index) => {
                                    const isCurrent = !step.done && steps.slice(0, index).every((s) => s.done);
                                    return (
                                        <li key={step.label} className={`rounded border-t-2 pt-2 text-xs ${step.done ? 'border-emerald-400 text-emerald-300' : isCurrent ? 'border-amber-400 text-amber-300' : 'border-gray-700 text-gray-500'}`}>
                                            {step.done ? '✓' : `${index + 1}.`} {step.label}
                                        </li>
                                    );
                                })}
                            </ol>
                        )}

                        <p className={`mt-4 text-sm ${admissionStatus === 'denied' ? 'text-rose-300' : isAdmitted ? 'text-emerald-300' : 'text-gray-200'}`}>
                            {nextStepMessage}
                        </p>

                        <button
                            onMouseEnter={() => !primaryAction.disabled && playProceduralSound('button')}
                            onClick={primaryAction.onClick}
                            disabled={primaryAction.disabled}
                            className={`mt-3 w-full px-3 py-3 rounded font-bold transition-all ${primaryAction.disabled ? 'bg-black border border-gray-600 text-gray-400 cursor-not-allowed' : 'bg-cyan-600 text-black hover:bg-cyan-500'}`}
                        >
                            {primaryAction.label}
                        </button>

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            {isWaitingAdmission && (
                                <button
                                    onMouseEnter={() => playProceduralSound('button')}
                                    onClick={handleReady}
                                    title="Mostra ao gestor que você já pode começar"
                                    className={`flex-1 px-3 py-2 rounded text-sm font-bold transition-all ${ready ? 'bg-amber-500 text-black' : 'bg-black border border-amber-500 text-amber-300 hover:bg-amber-500/10'}`}
                                >
                                    {ready ? '✓ Gestor avisado' : 'Estou pronto'}
                                </button>
                            )}
                            {isPrivileged && isAdmitted && (
                                <button
                                    onMouseEnter={() => playProceduralSound('button')}
                                    onClick={joinOperationRoom}
                                    title="Ativa a operação e abre o console de gestor"
                                    className="flex-1 px-3 py-2 rounded text-sm font-bold bg-black border border-cyan-600 text-cyan-200 hover:bg-cyan-600/10 transition-colors"
                                >
                                    Assumir comando da sala
                                </button>
                            )}
                            <button
                                onMouseEnter={() => playProceduralSound('button')}
                                onClick={onClose}
                                className="flex-1 px-3 py-2 rounded text-sm font-bold bg-black border border-gray-600 text-gray-400 hover:bg-gray-600/10 transition-colors"
                            >
                                Voltar
                            </button>
                        </div>

                        <div className="mt-4 border-t border-white/5 pt-3">
                            <div className="text-xs text-gray-400">Quem está na sala ({participantsList.length})</div>
                            {participantsList.length === 0 && (
                                <div className="mt-2 text-xs text-gray-500">Ninguém conectado ainda.</div>
                            )}
                            <ul className="mt-2 space-y-2">
                                {participantsList.map((p) => {
                                    const operationalRole = operationalRoles[p.operatorId] || 'observer';
                                    const isSelf = isCurrentUserPayload(p, user);
                                    const canAssignThisRole = canAssignRoles && !isSelf;
                                    return (
                                        <li key={p.operatorId || p.id} className="flex flex-wrap justify-between items-center gap-2 text-sm">
                                            <span className="text-cyan-200" title={p.operatorId || undefined}>
                                                {participantDisplayName(p) || p.id}{isSelf ? ' (você)' : ''}
                                            </span>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className={`text-xs ${p.admitted ? 'text-emerald-300' : 'text-amber-300'}`}>
                                                    {p.admitted ? 'Liberado' : 'Aguardando'}{p.ready ? ' • Pronto' : ''}
                                                </span>
                                                {!canAssignThisRole && (
                                                    <span className="text-xs text-gray-400" title={p.role || undefined}>{operationalRoleLabel(operationalRole)}</span>
                                                )}
                                                {canAssignThisRole && (
                                                    <select
                                                        value={operationalRole}
                                                        onChange={(e) => handleAssignRole(p.operatorId, e.target.value)}
                                                        onMouseEnter={() => playProceduralSound('button')}
                                                        className="text-xs bg-black/50 border border-cyan-600/30 text-cyan-100 rounded px-1 py-0.5"
                                                        title="Definir a função desta pessoa na operação"
                                                    >
                                                        {ASSIGNABLE_OPERATIONAL_ROLES.map((r) => (
                                                            <option key={r.value} value={r.value}>{r.label}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                {isPrivileged && !p.admitted && !isSelf && (
                                                    <button
                                                        onMouseEnter={() => playProceduralSound('button')}
                                                        onClick={() => handleAdmitUser(p.operatorId)}
                                                        className="px-2 py-1 text-xs bg-emerald-600 text-black rounded hover:bg-emerald-500 transition-colors"
                                                    >
                                                        Admitir
                                                    </button>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            {admitError && <div className="mt-2 text-xs text-rose-300">{admitError}</div>}
                        </div>

                        {eventFeed.length > 0 && (
                            <div className="mt-4 border-t border-white/5 pt-3">
                                <div className="text-xs text-gray-400">Movimentação recente</div>
                                <ul className="mt-2 max-h-20 overflow-auto space-y-1 text-xs text-gray-300">
                                    {eventFeed.slice(0, 4).map((entry) => (
                                        <li key={entry.id}>
                                            <span className="font-mono text-gray-500">{new Date(entry.timestamp).toLocaleTimeString('pt-BR')}</span> {safeText(entry.detail, { fallback: '', emptyFallback: '', invalidFallback: '' })}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </GlassCard>
                </motion.div>
            )}
        </AnimatePresence>, document.body
    );
}
